import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getBuilderUnitStats,
  updateBuilderBillingSettings
} from '../../services/api/implementation/impl';
import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Modal from '../../../shared/components/ui/Modal';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import EditBuilderCommission from '../forms/EditBuilderCommission';
import EditBuilderAiCredits from '../forms/EditBuilderAiCredits';
import EditBuilderAccessLock from '../forms/EditBuilderAccessLock';
import editIcon from '../../../shared/assets/svg/edit.svg';
import { Ban, CircleDollarSign, Loader2, Lock, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { getFixedHeaderClass } from '../../../shared/utils/sidebarLayout';
import DateFormat from '../../utils/DateFormat';
import '../../../shared/styles/modal.scss';

const formatSoldAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '₹ 0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const TABLE_COLUMNS = [
  {
    key: 'serialNumber',
    label: 'S.No',
    width: '4%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'builder_name',
    label: 'Builder Name',
    width: '14%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'plot_commission_charge',
    label: 'Plot Commission',
    width: '9%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'unit_commission_charge',
    label: 'Unit Commission',
    width: '9%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'units_sold',
    label: 'Units Sold',
    width: '7%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'units_vacant',
    label: 'Units Vacant',
    width: '7%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'total_sold_amount',
    label: 'Total Sold Amount',
    width: '13%',
    headerClassName: 'text-center',
    cellClassName: 'text-center whitespace-nowrap'
  },
  {
    key: 'invoice_billing_enabled',
    label: 'Billing Enabled',
    width: '10%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'invoice_billing_day',
    label: 'Billing Day',
    width: '7%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'last_invoice_date',
    label: 'Last Invoice Date',
    width: '9%',
    headerClassName: 'text-center',
    cellClassName: 'text-center whitespace-nowrap'
  },
  {
    key: 'access_status',
    label: 'Access',
    width: '8%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  },
  {
    key: 'actions',
    label: 'Action',
    width: '18%',
    headerClassName: 'text-center',
    cellClassName: 'text-center'
  }
];

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BuilderUnitStats = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAiCreditsModal, setOpenAiCreditsModal] = useState(false);
  const [openAccessLockModal, setOpenAccessLockModal] = useState(false);
  const [selectedBuilder, setSelectedBuilder] = useState(null);
  const [billingUpdatingBuilderId, setBillingUpdatingBuilderId] = useState(null);
  const userId = Cookies.get('userId');
  const { collapsed } = useSidebar();

  useEffect(() => {
    mixpanel.track('Super Admin Builder Unit Stats Viewed', {
      user_id: userId,
      page,
      timestamp: new Date().toISOString()
    });
  }, [userId, page]);

  const {
    data: unitStats,
    refetch,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['builderUnitStats', page],
    queryFn: () => getBuilderUnitStats(page, limit),
    keepPreviousData: true,
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch unit stats');
    }
  });

  const totalPages = unitStats?.meta ? Math.ceil(unitStats.meta.total / limit) : 1;

  const tableRows = (unitStats?.data ?? []).map((item, index) => ({
    builder_id: item.builder_id,
    serialNumber: (page - 1) * limit + index + 1,
    builder_name: item.builder_name?.trim() || 'N/A',
    plot_commission_charge: item.plot_commission_charge ?? 0,
    unit_commission_charge: item.unit_commission_charge ?? 0,
    units_sold: item.units_sold ?? 0,
    units_vacant: item.units_vacant ?? 0,
    total_sold_amount: formatSoldAmount(item.total_sold_amount),
    invoice_billing_enabled: item.invoice_billing_enabled ? 'Enabled' : 'Disabled',
    invoice_billing_enabled_raw: Boolean(item.invoice_billing_enabled),
    invoice_billing_day: item.invoice_billing_day ?? 'N/A',
    last_invoice_date: item.last_invoice_generated_at
      ? DateFormat(item.last_invoice_generated_at)
      : '-',
    access_status: item.access_status || 'active'
  }));

  const { mutate: updateBilling } = useMutation({
    mutationFn: ({ builderId, enabled, startDate }) =>
      updateBuilderBillingSettings(builderId, {
        invoice_billing_enabled: enabled,
        ...(enabled && startDate ? { invoice_billing_start_date: startDate } : {})
      }),
    onMutate: ({ builderId }) => {
      setBillingUpdatingBuilderId(builderId);
    },
    onSuccess: (_, { enabled }) => {
      toast.success(
        enabled ? 'Billing enabled successfully' : 'Billing stopped successfully'
      );
      refetch();
    },
    onError: (err, { enabled }) => {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          (enabled ? 'Failed to enable billing' : 'Failed to stop billing')
      );
    },
    onSettled: () => {
      setBillingUpdatingBuilderId(null);
    }
  });

  const handleEnableBilling = (builderId) => {
    const startDate = getTodayDateString();
    mixpanel.track('Super Admin Builder Billing Enabled', {
      user_id: userId,
      builder_id: builderId,
      invoice_billing_start_date: startDate,
      timestamp: new Date().toISOString()
    });
    updateBilling({ builderId, enabled: true, startDate });
  };

  const handleStopBilling = (builderId) => {
    mixpanel.track('Super Admin Builder Billing Stopped', {
      user_id: userId,
      builder_id: builderId,
      timestamp: new Date().toISOString()
    });
    updateBilling({ builderId, enabled: false });
  };

  const handlePageChange = (newPage) => {
    mixpanel.track('Super Admin Builder Unit Stats Pagination', {
      user_id: userId,
      from_page: page,
      to_page: newPage,
      timestamp: new Date().toISOString()
    });
    setPage(newPage);
  };

  const handleEdit = (builderId) => {
    const builder = unitStats?.data?.find(
      (item) => String(item.builder_id) === String(builderId)
    );
    if (!builder) {
      toast.error('Builder not found');
      return;
    }
    setSelectedBuilder(builder);
    setOpenEditModal(true);
  };

  const handleAiCredits = (builderId) => {
    const builder = unitStats?.data?.find(
      (item) => String(item.builder_id) === String(builderId)
    );
    if (!builder) {
      toast.error('Builder not found');
      return;
    }
    setSelectedBuilder(builder);
    setOpenAiCreditsModal(true);
  };

  const handleAccessLock = (builderId) => {
    const builder = unitStats?.data?.find(
      (item) => String(item.builder_id) === String(builderId)
    );
    if (!builder) {
      toast.error('Builder not found');
      return;
    }
    setSelectedBuilder(builder);
    setOpenAccessLockModal(true);
  };

  const handleCloseAccessLockModal = () => {
    setOpenAccessLockModal(false);
    setSelectedBuilder(null);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedBuilder(null);
  };

  useEffect(() => {
    if (openEditModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [openEditModal]);

  const renderCell = (row, column) => {
    if (column.key === 'actions') {
      const isUpdatingBilling = billingUpdatingBuilderId === row.builder_id;

      return (
        <div className="flex justify-center items-center gap-2">
          {!row.invoice_billing_enabled_raw ? (
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] transition-colors disabled:opacity-60"
              title="Enable billing"
              disabled={isUpdatingBilling}
              onClick={() => handleEnableBilling(row.builder_id)}>
              {isUpdatingBilling ? (
                <Loader2 className="h-4 w-4 text-customBlue animate-spin" />
              ) : (
                <CircleDollarSign className="h-4 w-4 text-customBlue" />
              )}
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] transition-colors disabled:opacity-60"
              title="Stop billing"
              disabled={isUpdatingBilling}
              onClick={() => handleStopBilling(row.builder_id)}>
              {isUpdatingBilling ? (
                <Loader2 className="h-4 w-4 text-red-500 animate-spin" />
              ) : (
                <Ban className="h-4 w-4 text-red-500" />
              )}
            </button>
          )}
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] transition-colors"
            title="AI credits & recharge"
            onClick={() => handleAiCredits(row.builder_id)}>
            <Sparkles className="h-4 w-4 text-[#1E4A53]" />
          </button>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] transition-colors"
            title="Edit commission"
            onClick={() => handleEdit(row.builder_id)}>
            <img src={editIcon} alt="Edit" className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] transition-colors"
            title="Limit or lock access"
            onClick={() => handleAccessLock(row.builder_id)}>
            <Lock
              className={`h-4 w-4 ${
                row.access_status === 'active' ? 'text-gray-500' : 'text-red-500'
              }`}
            />
          </button>
        </div>
      );
    }

    if (column.key === 'invoice_billing_enabled') {
      return (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            row.invoice_billing_enabled === 'Enabled'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-600'
          }`}>
          {row.invoice_billing_enabled}
        </span>
      );
    }

    if (column.key === 'access_status') {
      const styles = {
        active: 'bg-green-100 text-green-800',
        limited: 'bg-amber-100 text-amber-800',
        locked: 'bg-red-100 text-red-700'
      };
      const labels = { active: 'Full', limited: 'Limited', locked: 'Locked' };
      return (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            styles[row.access_status] || styles.active
          }`}>
          {labels[row.access_status] || labels.active}
        </span>
      );
    }

    if (column.key === 'builder_name') {
      return (
        <span className="inline-block truncate max-w-[12rem] text-center">
          {row[column.key]}
        </span>
      );
    }

    return row[column.key];
  };

  return (
    <>
      {openEditModal && selectedBuilder ? (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Edit commission"
          onClose={handleCloseEditModal}>
          <EditBuilderCommission
            builder={selectedBuilder}
            onClose={handleCloseEditModal}
            onSuccess={refetch}
          />
        </Modal>
      ) : null}

      {openAiCreditsModal && selectedBuilder ? (
        <Modal
          width={42}
          name="AI credits"
          onClose={() => {
            setOpenAiCreditsModal(false);
            setSelectedBuilder(null);
          }}>
          <EditBuilderAiCredits
            builder={selectedBuilder}
            onClose={() => {
              setOpenAiCreditsModal(false);
              setSelectedBuilder(null);
            }}
            onSuccess={() => refetch()}
          />
        </Modal>
      ) : null}

      {openAccessLockModal && selectedBuilder ? (
        <Modal width={42} name="Account access" onClose={handleCloseAccessLockModal}>
          <EditBuilderAccessLock
            builder={selectedBuilder}
            onClose={handleCloseAccessLockModal}
            onSuccess={() => refetch()}
          />
        </Modal>
      ) : null}

      <Layout>
        <Loader isVisible={isLoading} />

        <div className={getFixedHeaderClass(collapsed)}>
          <Header data={{ name: 'Unit Stats' }} />
        </div>

        <GridWrapper width={100}>
          <div className="mt-[5.5rem] flex flex-col h-[calc(100vh-7rem)] pb-3">
            <div className="bg-white border border-[#E7EBF1] rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
              <div className="overflow-auto flex-1 min-h-0">
                <table className="w-full min-w-[78rem] table-fixed border-collapse text-sm">
                  <colgroup>
                    {TABLE_COLUMNS.map((column) => (
                      <col key={column.key} style={{ width: column.width }} />
                    ))}
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-[#F8FAFC] border-b border-[#E7EBF1] text-[#64748B] font-semibold">
                    <tr>
                      {TABLE_COLUMNS.map((column) => (
                        <th
                          key={column.key}
                          className="px-3 py-3 font-semibold text-center">
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.length > 0 ? (
                      tableRows.map((row) => (
                        <tr
                          key={row.builder_id}
                          className="border-b border-[#E7EBF1] hover:bg-gray-50 transition-colors">
                          {TABLE_COLUMNS.map((column) => (
                            <td
                              key={column.key}
                              className={`px-3 py-3 text-gray-800 align-middle text-center ${column.cellClassName}`}>
                              {renderCell(row, column)}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={TABLE_COLUMNS.length}
                          className="px-4 py-10 text-center text-gray-400">
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <PaginationComponent
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              isFetching={isFetching}
            />
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default BuilderUnitStats;
